"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useAuth } from "@/src/contexts/AuthContext";
import { useCart } from "@/src/contexts/CartContext";
import { useTenant } from "@/src/contexts/TenantContext";
import { LoginStep } from "@/src/app/shop/checkout/components/LoginStep";
import { getAddresses } from "@/src/services/address.service";
import { searchProductsForStore, type SearchResult } from "@/src/services/product.service";
import type { Address } from "@/src/types/address";
import { addressLocationText, setSelectedCity, setSelectedCoords } from "@/src/constant/serviceArea/serviceArea";
import { SafeImage } from "@/src/components/SafeImage";
import { SubscriptionsNavItem } from "./SubscriptionsNavItem";

export const Header: React.FC = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { storeName, logoUrl, outletId: tenantOutletId, tenantId, prefix: tenantPrefix, minCartValue, city, topOfferBannerOffers } = useTenant();

  const outletId = tenantOutletId || searchParams.get("outletId") || "";
  const prefix = tenantPrefix || searchParams.get("prefix") || "";
  const { isAuthenticated, name, phoneNumber, logout, userId, token } = useAuth();
  const { getItemCount } = useCart();
  const [showLogin, setShowLogin] = useState(false);
  const [showAddressBox, setShowAddressBox] = useState(false);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const addressBoxRef = useRef<HTMLDivElement>(null);

  const cartCount = getItemCount();
  const displayName = name || (phoneNumber ? `•••• ${phoneNumber.slice(-4)}` : null);
  const initials =
    (name?.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase()) ||
    (phoneNumber ? phoneNumber.slice(-2) : null);

  // ── Search ────────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);
  const mobileSearchRef = useRef<HTMLDivElement>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeSearch = useCallback(() => {
    setSearchExpanded(false);
    setSearchQuery("");
    setSearchResults([]);
    setSearchOpen(false);
  }, []);

  const toggleSearch = useCallback(() => {
    if (searchExpanded) {
      closeSearch();
    } else {
      setSearchExpanded(true);
      setTimeout(() => {
        if (window.innerWidth >= 768) {
          searchInputRef.current?.focus();
        } else {
          mobileSearchInputRef.current?.focus();
        }
      }, 50);
    }
  }, [searchExpanded, closeSearch]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchExpanded(true);
        setTimeout(() => searchInputRef.current?.focus(), 50);
      }
      if (e.key === "Escape" && searchExpanded) {
        closeSearch();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [searchExpanded, closeSearch]);

  // Close search on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const inDesktop = searchRef.current?.contains(target);
      const inMobile = mobileSearchRef.current?.contains(target);
      if (!inDesktop && !inMobile) {
        setSearchOpen(false);
        setSearchExpanded(false);
        setSearchQuery("");
        setSearchResults([]);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSearchChange = useCallback((val: string) => {
    setSearchQuery(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    // Either scope works: tenant-wide when we know the store, outlet-scoped
    // otherwise. Requiring outletId alone would mute search on a tenant-wide render.
    if (!val.trim() || (!outletId && !tenantId)) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const results = await searchProductsForStore(val.trim(), { tenantId, outletId });
        setSearchResults(results);
        setSearchOpen(true);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  }, [outletId, tenantId]);

  const handleResultClick = (result: SearchResult) => {
    closeSearch();
    /* The result's OWN branch, not the header's context outlet. Search now spans
     * every branch the store runs, so stamping the context outlet here would
     * open the detail page against a branch that does not stock the product. */
    const target = result.outletId || outletId;
    router.push(`/shop/product/${result.id}?prefix=${prefix}&outletId=${target}`);
  };

  // Close address box on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (addressBoxRef.current && !addressBoxRef.current.contains(e.target as Node)) {
        setShowAddressBox(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Load + restore selected address on mount and whenever auth is ready
  useEffect(() => {
    if (!userId || !token) return;
    (async () => {
      try {
        const list = await getAddresses(token, userId);
        setAddresses(list);
        if (list.length === 0) return;
        const storedId = Number(localStorage.getItem("selected_address_id"));
        const restored = storedId ? list.find((a) => a.id === storedId) : null;
        const toSelect = restored ?? list[0];
        setSelectedAddress(toSelect);
        localStorage.setItem("selected_address_id", String(toSelect.id));
        setSelectedCoords(toSelect.coordinates);
      } catch { /* non-fatal */ }
    })();
  }, [userId, token]);

  /* Mirror the selected address's city so delivery-restricted outlets can
   * gate add-to-cart without refetching addresses everywhere. */
  useEffect(() => {
    // Logged out: forget the city so a stale one can't gate a guest's cart.
    setSelectedCity(isAuthenticated ? addressLocationText(selectedAddress) : null);
    setSelectedCoords(isAuthenticated ? selectedAddress?.coordinates : null);
    window.dispatchEvent(new Event("selected_city_changed"));
  }, [selectedAddress, isAuthenticated]);

  useEffect(() => {
    if (!userId || !token) return;
    const handler = async () => {
      try {
        const list = await getAddresses(token, userId);
        setAddresses(list);
        if (list.length === 0) { setSelectedAddress(null); return; }
        const storedId = Number(localStorage.getItem("selected_address_id"));
        const restored = storedId ? list.find((a) => a.id === storedId) : null;
        setSelectedAddress(restored ?? list[0]);
      } catch { /* non-fatal */ }
    };
    window.addEventListener("address_changed", handler);
    return () => window.removeEventListener("address_changed", handler);
  }, [userId, token]);

  const handleOpenAddressBox = async () => {
    setShowAddressBox((prev) => !prev);
    if (!showAddressBox && userId && token) {
      setLoadingAddresses(true);
      try {
        const list = await getAddresses(token, userId);
        setAddresses(list);
        if (list.length > 0) {
          const storedId = Number(localStorage.getItem("selected_address_id"));
          const restored = storedId ? list.find((a) => a.id === storedId) : null;
          const toSelect = restored ?? list[0];
          setSelectedAddress(toSelect);
          localStorage.setItem("selected_address_id", String(toSelect.id));
          setSelectedCoords(toSelect.coordinates);
        }
      } catch { /* non-fatal */ }
      setLoadingAddresses(false);
    }
  };

  // ── Shared search results list ─────────────────────────────────────────────
  const SearchResultsList = () => (
    <ul className="max-h-[360px] overflow-y-auto">
      {searchResults.map((r) => {
        const mrp = parseFloat(r.mrpPrice || "0");
        const price = parseFloat(r.sellingPrice || "0");
        const disc = mrp > price && mrp > 0 ? Math.round(((mrp - price) / mrp) * 100) : 0;
        const hasImage = r.thumbnail?.startsWith("http");
        return (
          <li key={r.id}>
            <button
              onClick={() => handleResultClick(r)}
              className="flex w-full items-center gap-3 px-4 py-3 text-left transition"
              style={{ borderBottom: "1px solid var(--border-soft)" }}
              onMouseEnter={e => (e.currentTarget.style.background = "var(--surface-2)")}
              onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl"
                style={{ background: "linear-gradient(135deg,#F4F1EA,#ECE7DC)" }}>
                {hasImage
                  ? <SafeImage src={r.thumbnail} alt={r.name} className="h-full w-full object-cover" />
                  : <span className="text-[11px] font-bold" style={{ color: "rgba(53,73,48,0.3)" }}>
                      {r.name.slice(0, 2).toUpperCase()}
                    </span>}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13.5px] font-semibold" style={{ color: "var(--techno-black)" }}>{r.name}</p>
                <div className="mt-0.5 flex items-center gap-2">
                  {price > 0 && (
                    <span className="text-[12px] font-bold" style={{ color: "var(--coral-deep)" }}>₹{price}</span>
                  )}
                  {mrp > price && (
                    <span className="text-[11px] line-through" style={{ color: "var(--techno-black-50)" }}>₹{mrp}</span>
                  )}
                  {disc >= 5 && (
                    <span className="rounded px-1 text-[10px] font-bold"
                      style={{ background: "rgba(242, 98, 80,0.16)", color: "var(--coral-deep)" }}>
                      {disc}% off
                    </span>
                  )}
                </div>
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                style={{ color: "var(--techno-black-50)", flexShrink: 0 }}>
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </button>
          </li>
        );
      })}
    </ul>
  );

  const SearchIcon = ({ size = 20 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
    </svg>
  );

  const ClearButton = () => (
    <button
      onClick={() => { setSearchQuery(""); setSearchResults([]); setSearchOpen(false); }}
      className="shrink-0"
      style={{ color: "var(--techno-black-50)" }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>
  );

  const SpinnerIcon = () => (
    <svg className="animate-spin shrink-0" width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke="#F26250" strokeWidth="2" strokeLinecap="round">
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );

  // Honest store guarantees for the marquee ticker. Universal GrabLocal guarantees
  // are always present, plus store-specific signals (free-delivery threshold, and
  // same-day only when it actually applies to the buyer's city) — so it is never empty.
  const sameDayApplies =
    !!city &&
    (!selectedAddress?.city ||
      city.trim().toLowerCase().includes(selectedAddress.city.trim().toLowerCase()) ||
      selectedAddress.city.trim().toLowerCase().includes(city.trim().toLowerCase()));
  const defaultTickerItems = [
    { key: "verified", label: "Verified by GrabLocal", tone: "var(--gold)", icon: (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6z" /><path d="M9 12l2 2 4-4" /></svg>) },
    { key: "returns", label: "Easy 7-day returns", tone: "var(--coral)", icon: (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 2.6-6.3L3 8" /><path d="M3 3v5h5" /></svg>) },
    ...(minCartValue ? [{ key: "free", label: `Free delivery over ₹${minCartValue.toLocaleString("en-IN")}`, tone: "var(--coral)", icon: (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1.5" y="6" width="12" height="10" rx="1.5" /><path d="M13.5 9H17l4 4v3h-7.5z" /><circle cx="6" cy="18.5" r="1.6" /><circle cx="18" cy="18.5" r="1.6" /></svg>) }] : []),
    ...(sameDayApplies ? [{ key: "sameday", label: `Same-day delivery in ${city}`, tone: "var(--gold)", icon: (<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M13 2L3 14h7l-1 8 10-12h-7z" /></svg>) }] : []),
    { key: "protect", label: "Buyer protection on every order", tone: "var(--gold)", icon: (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>) },
  ];
  const tagIcon = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><circle cx="7" cy="7" r="1" /></svg>;
  const tickerItems = topOfferBannerOffers?.length
    ? topOfferBannerOffers.map((o, i) => ({
        key: `offer-${i}`,
        label: [o.bold, o.body].filter(Boolean).join(' '),
        tone: 'var(--turmeric)',
        icon: tagIcon,
      }))
    : defaultTickerItems;
  // Repeat the guarantees enough that ONE half of the track always exceeds the
  // widest viewport — otherwise a short list leaves an empty gap at the loop seam.
  // Duration scales with the instance count so the px/sec speed stays constant
  // (and brisk) regardless of how many guarantees a given store surfaces.
  const tickerRepeats = Math.max(4, Math.ceil(12 / Math.max(1, tickerItems.length)));
  const tickerHalf = Array.from({ length: tickerRepeats }).flatMap(() => tickerItems);
  const tickerDuration = Math.round(tickerHalf.length * 1.6);

  return (
    <>
      {/* ── Guarantee ticker (two-tier) — a non-sticky marquee that scrolls away
          so the 64px sticky main row below keeps every downstream sticky offset
          valid. Built from honest, universal guarantees so it is never empty. ── */}
      <div className="gl-ticker" style={{ background: "var(--navy)", color: "rgba(255,255,255,0.92)" }}>
        <div className="gl-ticker-track" style={{ animationDuration: `${tickerDuration}s` }}>
          {[0, 1].map((dup) =>
            tickerHalf.map((it, i) => (
              <span key={`${dup}-${i}-${it.key}`} className="inline-flex items-center" aria-hidden={dup === 0 && i < tickerItems.length ? undefined : true} style={{ flexShrink: 0 }}>
                <span className="inline-flex items-center gap-1.5 px-5 text-[11.5px] font-medium" style={{ height: 32, lineHeight: "32px", whiteSpace: "nowrap" }}>
                  <span style={{ color: it.tone, display: "inline-flex", flexShrink: 0 }}>{it.icon}</span>
                  {it.label}
                </span>
                <span aria-hidden="true" style={{ width: 1, height: 12, background: "rgba(255,255,255,0.18)", flexShrink: 0 }} />
              </span>
            ))
          )}
        </div>
      </div>

      <header
        className="sticky top-0 z-50 border-b"
        style={{
          background: "rgba(250, 248, 243, 0.88)",
          backdropFilter: "saturate(180%) blur(20px)",
          WebkitBackdropFilter: "saturate(180%) blur(20px)",
          borderColor: "var(--border-soft)",
        }}
      >
        <div className="gl-container">
          {/* ── Main nav row ───────────────────────────────────────────────
              Layout: [logo] [centered desktop search (always visible)] [right actions]
              The centered search eliminates the dead-space gap that appeared on
              wider viewports when search was hidden behind an icon and the
              right cluster used ml-auto to push everything right. */}
          <div className="flex h-16 items-center gap-3 md:gap-4">
            {/* Brand logo */}
            <Link
              href={`/shop?prefix=${prefix}`}
              aria-label={storeName}
              className="flex shrink-0 items-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--coral)] focus-visible:ring-offset-[color:var(--surface)]"
            >
              {logoUrl ? (
                <SafeImage
                  src={logoUrl}
                  alt={storeName}
                  style={{ height: 40, width: "auto", maxHeight: "100%", flexShrink: 0, objectFit: "contain" }}
                />
              ) : (
                <span style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 22, color: "var(--techno-black)", letterSpacing: "-0.01em" }}>
                  {storeName}
                </span>
              )}
            </Link>

            {/* ── Desktop persistent search ──────────────────────────────
                Always visible on md+, occupies the middle so logo and right
                actions sit at comfortable distance. Click-to-expand pattern
                survives only on mobile (below). */}
            <div className="relative hidden md:flex flex-1 max-w-[520px] mx-auto" ref={searchRef}>
              <div
                className="flex w-full items-center gap-2.5 rounded-full border px-4 py-2.5 transition focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-[color:var(--coral)] focus-within:ring-offset-[color:var(--surface)]"
                style={{
                  background: "var(--surface)",
                  borderColor: searchOpen ? "var(--coral)" : "var(--border)",
                  boxShadow: searchOpen ? "0 0 0 3px rgba(242, 98, 80,0.12)" : "none",
                }}
              >
                {searchLoading ? <SpinnerIcon /> : (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" style={{ color: "var(--techno-black-50)", flexShrink: 0 }}>
                    <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
                  </svg>
                )}
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={e => handleSearchChange(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
                  placeholder={`Search ${storeName || ""}…`}
                  className="min-w-0 flex-1 border-none bg-transparent outline-none"
                  style={{ fontSize: 14, color: "var(--techno-black)" }}
                />
                {searchQuery ? <ClearButton /> : (
                  <span className="hidden rounded-md border px-1.5 py-0.5 text-[11px] font-semibold lg:inline"
                    style={{ color: "var(--techno-black-50)", borderColor: "var(--border)", flexShrink: 0 }}>
                    ⌘ K
                  </span>
                )}
              </div>

              {/* Persistent-search results dropdown */}
              {searchOpen && searchResults.length > 0 && (
                <div
                  className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 overflow-hidden rounded-2xl"
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--border-soft)",
                    boxShadow: "0 16px 48px rgba(0,0,0,0.12)",
                  }}
                >
                  <p className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-widest"
                    style={{ color: "var(--techno-black-50)", borderBottom: "1px solid var(--border-soft)" }}>
                    {searchResults.length} result{searchResults.length !== 1 ? "s" : ""}
                  </p>
                  <SearchResultsList />
                </div>
              )}
              {searchOpen && !searchLoading && searchQuery.trim() && searchResults.length === 0 && (
                <div
                  className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 rounded-2xl px-4 py-5 text-center"
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--border-soft)",
                    boxShadow: "0 16px 48px rgba(0,0,0,0.12)",
                  }}
                >
                  <p className="text-sm" style={{ color: "var(--techno-black-50)" }}>
                    No products found for &ldquo;{searchQuery}&rdquo;
                  </p>
                </div>
              )}
            </div>

            {/* Mobile inline search — outer wrapper uses md:hidden (no inline display style here) */}
            <div className={`md:hidden overflow-hidden min-w-0 ${searchExpanded ? "flex-1" : ""}`}>
              <div
                ref={mobileSearchRef}
                style={{
                  display: "grid",
                  gridTemplateColumns: searchExpanded ? "1fr" : "0fr",
                  transition: "grid-template-columns 280ms cubic-bezier(0.4,0,0.2,1)",
                }}
              >
                <div className="min-w-0 overflow-hidden">
                  <div
                    className="flex items-center gap-2.5 rounded-full border px-4 py-2.5 mr-1"
                    style={{
                      background: "var(--surface)",
                      borderColor: "var(--coral)",
                      boxShadow: "0 0 0 3px rgba(242, 98, 80,0.12)",
                    }}
                  >
                    {searchLoading ? <SpinnerIcon /> : (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" style={{ color: "var(--techno-black-50)", flexShrink: 0 }}>
                        <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
                      </svg>
                    )}
                    <input
                      ref={mobileSearchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={e => handleSearchChange(e.target.value)}
                      onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
                      placeholder={`Search ${storeName || ""}…`}
                      className="min-w-0 flex-1 border-none bg-transparent outline-none"
                      style={{ fontSize: 14, color: "var(--techno-black)" }}
                    />
                    {searchQuery && <ClearButton />}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Right actions ──────────────────────────────────────────
                shrink-0 keeps the cluster tight; no more `ml-auto` because the
                centered search above already eats the slack. */}
            <div className="flex items-center gap-2 shrink-0">

              {/* Mobile-only search icon — toggles the inline mobile search.
                  Desktop uses the centered persistent search above. */}
              <button
                onClick={toggleSearch}
                aria-label="Search"
                className="flex h-10 w-10 items-center justify-center rounded-full transition md:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--coral)] focus-visible:ring-offset-[color:var(--surface)]"
                title="Search"
                style={{
                  color: searchExpanded ? "#F26250" : "var(--techno-black-70)",
                  background: searchExpanded ? "rgba(242, 98, 80,0.08)" : "transparent",
                }}
                onMouseEnter={e => { if (!searchExpanded) e.currentTarget.style.background = "var(--techno-black-10)"; }}
                onMouseLeave={e => { if (!searchExpanded) e.currentTarget.style.background = searchExpanded ? "rgba(242, 98, 80,0.08)" : "transparent"; }}
              >
                <SearchIcon />
              </button>

              {/* Subscriptions — sits immediately left of the location chip.
                  Renders as a labelled pill at lg+ and an icon button below,
                  where the location chip isn't shown at all. */}
              {isAuthenticated && <SubscriptionsNavItem prefix={prefix} />}

              {/* Location chip — desktop only, authenticated */}
              {isAuthenticated && (
                <div className="relative hidden lg:block" ref={addressBoxRef}>
                  <button
                    onClick={handleOpenAddressBox}
                    className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--coral)] focus-visible:ring-offset-[color:var(--surface)]"
                    style={{ background: "var(--cream)", border: "1px solid var(--border-soft)", color: "var(--techno-black-70)" }}
                    onMouseEnter={e => (e.currentTarget.style.background = "var(--surface-2)")}
                    onMouseLeave={e => (e.currentTarget.style.background = "var(--cream)")}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ color: selectedAddress ? "var(--coral-deep)" : "var(--techno-black-50)", flexShrink: 0 }}>
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z"/>
                    </svg>
                    <span>
                      Deliver to{" "}
                      <strong style={{ color: "var(--techno-black)", fontWeight: 600 }}>
                        {selectedAddress ? (selectedAddress.sublocality || selectedAddress.city || "your area") : "your area"}
                      </strong>
                    </span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                      style={{ opacity: 0.4, transform: showAddressBox ? "rotate(180deg)" : "none", transition: "transform 200ms" }}>
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </button>

                  {showAddressBox && (
                    <div
                      className="absolute left-0 top-[calc(100%+8px)] z-50 w-[320px] rounded-2xl bg-white shadow-xl border border-[#E5E2DA] overflow-hidden"
                      style={{ animation: "fadeUp 150ms ease" }}
                    >
                      <div className="px-4 py-3 border-b border-[#F0EDE6]">
                        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A8A8A3]">Deliver to</p>
                      </div>

                      {loadingAddresses ? (
                        <div className="flex items-center justify-center py-8 gap-2 text-sm text-[#8A8A85]">
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#F26250] border-t-transparent" />
                          Loading addresses…
                        </div>
                      ) : addresses.length === 0 ? (
                        <div className="px-4 py-6 text-center text-sm text-[#8A8A85]">
                          No saved addresses yet.
                        </div>
                      ) : (
                        <ul className="max-h-[260px] overflow-y-auto divide-y divide-[#F5F3EE]">
                          {addresses.map((addr) => {
                            const isSelected = selectedAddress?.id === addr.id;
                            return (
                              <li key={addr.id}>
                                <button
                                  onClick={() => {
                                    setSelectedAddress(addr);
                                    localStorage.setItem("selected_address_id", String(addr.id));
                                    setSelectedCoords(addr.coordinates);
                                    window.dispatchEvent(new Event("address_changed"));
                                    setShowAddressBox(false);
                                  }}
                                  className="flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-[#FBF9F6]"
                                >
                                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition"
                                    style={{ borderColor: isSelected ? "var(--coral)" : "#D0CCC4", background: isSelected ? "var(--coral)" : "transparent" }}>
                                    {isSelected && <span className="h-2 w-2 rounded-full bg-white" />}
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-[12px] font-bold capitalize text-[#1D1D1D]">
                                      {addr.tag || "Other"}{addr.sublocality ? ` · ${addr.sublocality}` : ""}
                                    </p>
                                    <p className="mt-0.5 truncate text-[11px] text-[#8A8A85]">
                                      {[addr.houseNo, addr.addressLine1, addr.city].filter(Boolean).join(", ")}
                                    </p>
                                  </div>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}

                      <div className="border-t border-[#F0EDE6] px-4 py-3">
                        <Link
                          href={`/shop/address?prefix=${prefix}`}
                          onClick={() => setShowAddressBox(false)}
                          className="flex w-full items-center justify-center gap-1.5 rounded-full bg-[var(--coral)] py-2.5 text-[12px] font-bold text-white transition hover:bg-[var(--coral-deep)]"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                          </svg>
                          Add new address
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* User + Cart — hidden on mobile while the search field is expanded
                  so the input gets the full row width (always shown on md+). */}
              <div className={`items-center gap-2 ${searchExpanded ? "hidden md:flex" : "flex"}`}>
              {isAuthenticated ? (
                <>
                  <Link
                    href={`/shop/profile?prefix=${prefix}`}
                    title={displayName ?? "My Account"}
                    aria-label={displayName ?? "My Account"}
                    className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full text-[13px] font-semibold transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--coral)] focus-visible:ring-offset-[color:var(--surface)]"
                    style={{ background: "var(--navy)", color: "#fff" }}
                  >
                    {initials ?? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                        <circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
                      </svg>
                    )}
                  </Link>
                  <button
                    onClick={logout}
                    aria-label="Logout"
                    className="flex h-10 w-10 items-center justify-center rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--coral)] focus-visible:ring-offset-[color:var(--surface)]"
                    title="Logout"
                    style={{ color: "var(--techno-black-70)" }}
                    onMouseEnter={e => (e.currentTarget.style.background = "var(--techno-black-10)")}
                    onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowLogin(true)}
                  aria-label="Login"
                  className="flex h-10 w-10 items-center justify-center rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--coral)] focus-visible:ring-offset-[color:var(--surface)]"
                  title="Login"
                  style={{ color: "var(--techno-black-70)" }}
                  onMouseEnter={e => (e.currentTarget.style.background = "var(--techno-black-10)")}
                  onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                    <circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
                  </svg>
                </button>
              )}

              {/* Cart */}
              <Link
                href={`/shop/checkout${prefix ? `?prefix=${prefix}` : ""}`}
                aria-label={cartCount > 0 ? `Cart, ${cartCount} item${cartCount !== 1 ? "s" : ""}` : "Cart"}
                className="relative flex h-10 w-10 items-center justify-center rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--coral)] focus-visible:ring-offset-[color:var(--surface)]"
                style={{ color: "var(--techno-black-70)" }}
                onMouseEnter={e => (e.currentTarget.style.background = "var(--techno-black-10)")}
                onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
                  <path d="M3 6h18" /><path d="M16 10a4 4 0 01-8 0" />
                </svg>
                {cartCount > 0 && (
                  <span
                    className="absolute right-1 top-1 flex min-w-[18px] items-center justify-center rounded-full px-1 text-[10.5px] font-bold text-white"
                    style={{ height: 18, background: "var(--coral)", border: "2px solid var(--rev-white)" }}
                  >
                    {cartCount}
                  </span>
                )}
              </Link>
              </div>
            </div>
          </div>

          {/* ── Mobile search results (inline, below nav row) ──────────── */}
          {searchOpen && searchResults.length > 0 && (
            <div className="pb-2 md:hidden">
              <div
                className="overflow-hidden rounded-2xl"
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border-soft)",
                  boxShadow: "0 16px 48px rgba(0,0,0,0.12)",
                }}
              >
                <p className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-widest"
                  style={{ color: "var(--techno-black-50)", borderBottom: "1px solid var(--border-soft)" }}>
                  {searchResults.length} result{searchResults.length !== 1 ? "s" : ""}
                </p>
                <SearchResultsList />
              </div>
            </div>
          )}
          {searchOpen && !searchLoading && searchQuery.trim() && searchResults.length === 0 && (
            <div className="pb-2 md:hidden">
              <div
                className="rounded-2xl px-4 py-5 text-center"
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border-soft)",
                  boxShadow: "0 16px 48px rgba(0,0,0,0.12)",
                }}
              >
                <p className="text-sm" style={{ color: "var(--techno-black-50)" }}>
                  No products found for &ldquo;{searchQuery}&rdquo;
                </p>
              </div>
            </div>
          )}
        </div>
      </header>

      {showLogin && (
        <LoginStep
          outletId={outletId}
          onComplete={() => setShowLogin(false)}
          onClose={() => setShowLogin(false)}
        />
      )}
    </>
  );
};
